// Revnu blog posts, as written at build time by scripts/revnu-sync.js into
// /blog/_revnu/ (public, key-free JSON). The browser never talks to Revnu:
// the API token stays in the build. Crawlers get the prerendered HTML; these
// files let the SPA render the same page on client-side navigation.

export const REVNU_DATA_BASE = '/blog/_revnu';

const EMPTY_MANIFEST = { posts: [], replacedLegacySlugs: [] };
let manifestPromise = null;

// Missing manifest (e.g. `npm start`, where the dev server answers with the
// HTML shell) means "no Revnu posts", never an error.
export function loadRevnuManifest() {
  if (!manifestPromise) {
    manifestPromise = fetch(`${REVNU_DATA_BASE}/index.json`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((m) => (m && Array.isArray(m.posts)
        ? { ...EMPTY_MANIFEST, ...m, replacedLegacySlugs: m.replacedLegacySlugs || [] }
        : EMPTY_MANIFEST));
  }
  return manifestPromise;
}

export function resetRevnuManifestCache() {
  manifestPromise = null;
}

// "/blog/foo/" → "/blog/foo"
export function normalizeBlogPath(pathname) {
  const p = String(pathname || '').replace(/\/+$/, '');
  return p || '/';
}

export function findRevnuPost(manifest, pathname) {
  const route = normalizeBlogPath(pathname);
  return (manifest.posts || []).find((p) => p.route === route) || null;
}

export async function loadRevnuPost(slug) {
  const r = await fetch(`${REVNU_DATA_BASE}/posts/${encodeURIComponent(slug)}.json`);
  if (!r.ok) throw new Error('Blog post not found');
  return r.json();
}

// Manifest entry → the card shape BlogList renders for legacy posts. Revnu
// has no tags, so a Revnu copy of an imported legacy post keeps that post's
// tags — the /blog topic filters keep working for it.
export function toListPost(p, tags = []) {
  return {
    slug: p.slug,
    href: p.route,
    title: p.title,
    description: p.description || '',
    author: p.author,
    date: p.date,
    coverImage: p.image,
    readingTime: p.readingTime,
    tags,
  };
}

// Newest first; a post without a date sorts last instead of breaking the sort.
export const byDateDesc = (a, b) => (Date.parse(b.date) || 0) - (Date.parse(a.date) || 0);

// Posts that belong on /blog: legacy markdown posts (minus any whose URL a
// Revnu import now serves) plus every Revnu post, newest first.
export function mergeBlogPosts(legacyPosts, manifest) {
  const replaced = new Set(manifest.replacedLegacySlugs || []);
  const legacyTags = new Map((legacyPosts || []).map((p) => [`/${p.slug}`, p.tags || []]));
  const legacy = (legacyPosts || [])
    .filter((p) => !replaced.has(p.slug))
    .map((p) => ({ ...p, href: `/blog/${p.slug}` }));
  const revnu = (manifest.posts || []).map((p) => toListPost(p, legacyTags.get(p.canonicalPath) || []));
  return [...legacy, ...revnu].sort(byDateDesc);
}

// JSON-LD goes inside <script>; a "</script>" or "<!--" in a string must not
// end the element early. "<" parses back to "<", so the structured data
// is unchanged.
export function jsonLdScript(entry) {
  return JSON.stringify(entry).replace(/</g, '\\u003c');
}
